// Хук для получения текущего пользователя
import { useEffect } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { supabase } from '@/lib';
import { parseError, userQueryKeys, handleApiError, setSentryUser } from '@/lib';
import type { User } from '../model';
import { mapProfileRowToUser } from '../lib';

async function getCurrentUser(): Promise<User | null> {
  try {
    // Проверяем сессию
    const { data: sessionData } = await supabase.auth.getSession();
    
    if (!sessionData.session) {
      return null;
    }

    const authUser = sessionData.session.user;

    // Получаем профиль из БД.
    // maybeSingle вместо single: если профиля ещё нет (триггер БД не успел
    // его создать сразу после signUp), возвращаем null без PGRST116 ошибки.
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', authUser.id)
      .maybeSingle();

    if (error) {
      handleApiError(error, { consolePrefix: 'Ошибка загрузки профиля пользователя' });
      throw parseError(error);
    }

    if (!profile) {
      return null;
    }

    // Обновляем last_seen_at — фиксируем открытие приложения.
    // Не ждём результата и не блокируем загрузку — fire and forget.
    //
    // Раньше last_seen_at лежал в profiles, но был вынесен в user_private_data
    // чтобы другие юзеры не могли видеть когда ты был онлайн через прямой
    // supabase.from('profiles'). Используем upsert на случай если у юзера
    // ещё нет записи (триггер create_user_private_data создаёт её при
    // INSERT в profiles, но для старых юзеров записи может не быть).
    supabase
      .from('user_private_data')
      .upsert(
        { user_id: authUser.id, last_seen_at: new Date().toISOString() },
        { onConflict: 'user_id' },
      )
      .then(({ error: updateError }) => {
        if (updateError) {
          console.warn('Не удалось обновить last_seen_at:', updateError.message);
        }
      });

    return mapProfileRowToUser(profile, authUser.email);
  } catch (error) {
    handleApiError(error, { consolePrefix: 'Ошибка в getCurrentUser' });
    throw parseError(error);
  }
}

export function useCurrentUser(): UseQueryResult<User | null, Error> {
  const query = useQuery({
    queryKey: userQueryKeys.currentUser(),
    queryFn: getCurrentUser,
    staleTime: 10 * 60 * 1000, // 10 минут - данные профиля редко меняются
    retry: (failureCount, error) => {
      // Повторяем только сетевые ошибки
      const appError = parseError(error);
      if (appError.code === 'NETWORK_ERROR' || appError.code === 'TIMEOUT') {
        return failureCount < 2;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
  });

  // Синхронизация текущего юзера с Sentry (GDPR: шлём только id).
  // Выполняется в фоне — не блокирует рендер.
  useEffect(() => {
    setSentryUser(query.data?.id ?? null);
  }, [query.data?.id]);

  return query;
}